import { useState, useEffect } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { Box, CircularProgress, Typography, useTheme, useMediaQuery, Button, ButtonGroup } from '@mui/material';
import { tokens } from '../../theme';
import axios from 'axios';

const DecBurnCategoriesChart = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullData, setFullData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('30'); // Default 30 days
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Custom theme for the bar chart
  const customChartTheme = {
    axis: {
      ticks: {
        text: {
          fill: colors.grey[100],
          fontSize: isMobile ? 9 : 11
        }
      },
      legend: {
        text: {
          fill: colors.grey[100],
          fontSize: isMobile ? 10 : 12
        }
      }
    },
    grid: {
      line: {
        stroke: colors.grey[700],
        strokeWidth: 1
      }
    },
    legends: {
      text: {
        fontSize: isMobile ? 9 : 11,
        fill: colors.grey[100],
      }
    },
    tooltip: {
      container: {
        background: colors.primary[700],
        color: colors.grey[100],
        fontSize: isMobile ? 11 : 13,
      }
    }
  };

  // Format numbers
  const formatNumber = (value) => {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`;
    } else if (value >= 1_000) {
      return `${(value / 1_000).toFixed(0)}K`;
    }
    return value.toFixed(0);
  };

  // Filter data based on selected time range
  const filterDataByTime = (data, days) => {
    if (days === 'all' || !data || data.length === 0) return data;
    
    const numDays = parseInt(days);
    return data.map(line => ({
      ...line,
      data: line.data.slice(-numDays) // Take last N days
    }));
  };

  // Fetch burn category history
  const fetchBurnCategories = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get('https://api.elmo-burns.com/api/v1/dec-burn-category-history/');
      
      if (response.data && response.data.results && response.data.results.length > 1) {
        // Sort by date ascending (oldest first) for line chart
        const sortedData = [...response.data.results].reverse();
        
        // Calculate DAILY changes for each category
        const dailyChanges = [];
        for (let i = 1; i < sortedData.length; i++) {
          const today = sortedData[i];
          const yesterday = sortedData[i - 1];
          
          dailyChanges.push({
            date: today.date,
            Shop: parseFloat(today.shop) - parseFloat(yesterday.shop),
            'Market Fee': parseFloat(today.market_burn_fee) - parseFloat(yesterday.market_burn_fee),
            Unbind: parseFloat(today.unbind_account) - parseFloat(yesterday.unbind_account),
            'Burn Event': parseFloat(today.burn_event) - parseFloat(yesterday.burn_event),
            'Guild Store': parseFloat(today.guild_store) - parseFloat(yesterday.guild_store),
            Energy: parseFloat(today.energy) - parseFloat(yesterday.energy),
            'Wild Pass': parseFloat(today.wild_pass) - parseFloat(yesterday.wild_pass),
            'Land Core': parseFloat(today.land_power_core) - parseFloat(yesterday.land_power_core),
            Other: parseFloat(today.other) - parseFloat(yesterday.other),
          });
        }
        
        // Transform to line chart format
        const categories = ['Shop', 'Market Fee', 'Unbind', 'Burn Event', 'Guild Store', 'Energy', 'Wild Pass', 'Land Core', 'Other'];
        const lineData = categories.map(category => ({
          id: category,
          data: dailyChanges.map(day => ({
            x: day.date,
            y: day[category]
          }))
        }));
        
        setFullData(lineData);
        setError(null);
      } else {
        throw new Error('Not enough data to calculate daily changes');
      }
      
    } catch (err) {
      console.error('Failed to fetch burn categories:', err);
      setError('Unable to load burn categories');
      setFullData([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Update chart data when filter or full data changes
  useEffect(() => {
    if (fullData.length > 0) {
      const days = timeFilter === 'all' ? 'all' : timeFilter;
      const filteredData = filterDataByTime(fullData, days);
      setChartData(filteredData);
    }
  }, [timeFilter, fullData]);
  
  useEffect(() => {
    fetchBurnCategories();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchBurnCategories, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error" gutterBottom>{error}</Typography>
        <Typography variant="caption" color={colors.grey[400]}>Please try again later</Typography>
      </Box>
    );
  }

  if (chartData.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color={colors.grey[400]}>No data available yet</Typography>
      </Box>
    );
  }

  return (
    <Box height="100%" display="flex" flexDirection="column">
      {/* Time Filter Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <ButtonGroup 
          size={isMobile ? "small" : "medium"}
          variant="outlined"
          sx={{
            '& .MuiButton-root': {
              borderColor: colors.grey[700],
              color: colors.grey[100],
              '&:hover': {
                borderColor: colors.redAccent[500],
                backgroundColor: colors.redAccent[900],
              }
            }
          }}
        >
          <Button
            onClick={() => setTimeFilter('7')}
            sx={{
              backgroundColor: timeFilter === '7' ? colors.redAccent[800] : 'transparent',
              borderColor: timeFilter === '7' ? colors.redAccent[500] : colors.grey[700],
              fontWeight: timeFilter === '7' ? 'bold' : 'normal',
            }}
          >
            7D
          </Button>
          <Button
            onClick={() => setTimeFilter('14')}
            sx={{
              backgroundColor: timeFilter === '14' ? colors.redAccent[800] : 'transparent',
              borderColor: timeFilter === '14' ? colors.redAccent[500] : colors.grey[700],
              fontWeight: timeFilter === '14' ? 'bold' : 'normal',
            }}
          >
            14D
          </Button>
          <Button
            onClick={() => setTimeFilter('30')}
            sx={{
              backgroundColor: timeFilter === '30' ? colors.redAccent[800] : 'transparent',
              borderColor: timeFilter === '30' ? colors.redAccent[500] : colors.grey[700],
              fontWeight: timeFilter === '30' ? 'bold' : 'normal',
            }}
          >
            30D
          </Button>
          <Button
            onClick={() => setTimeFilter('all')}
            sx={{
              backgroundColor: timeFilter === 'all' ? colors.redAccent[800] : 'transparent',
              borderColor: timeFilter === 'all' ? colors.redAccent[500] : colors.grey[700],
              fontWeight: timeFilter === 'all' ? 'bold' : 'normal',
            }}
          >
            ALL
          </Button>
        </ButtonGroup>
      </Box>
      
      {/* Chart */}
      <Box flex="1" minHeight={0}>
        <ResponsiveLine
          data={chartData}
          theme={customChartTheme}
          margin={{ top: 20, right: isMobile ? 80 : 120, bottom: isMobile ? 60 : 80, left: isMobile ? 50 : 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 'auto',
            max: 'auto',
            stacked: false,
            reverse: false
          }}
          yFormat={value => formatNumber(value)}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: isMobile ? -45 : -30,
            legend: '',
            legendOffset: 36,
            legendPosition: 'middle',
            format: (value) => {
              const date = new Date(value);
              return isMobile ? `${date.getMonth() + 1}/${date.getDate()}` : value;
            }
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Daily Burns',
            legendOffset: isMobile ? -45 : -60,
            legendPosition: 'middle',
            format: formatNumber
          }}
          colors={[
            '#3b82f6',   // Shop - Blue
            '#10b981',   // Market Fee - Green
            '#8b5cf6',   // Unbind - Purple
            '#f59e0b',   // Burn Event - Orange
            '#ef4444',   // Guild Store - Red
            '#06b6d4',   // Energy - Cyan
            '#ec4899',   // Wild Pass - Pink
            '#f97316',   // Land Core - Orange-Red
            '#6366f1',   // Other - Indigo
          ]}
          pointSize={isMobile ? 4 : 6}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          enableArea={false}
          useMesh={true}
          enableSlices="x"
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: isMobile ? 75 : 110,
              translateY: 0,
              itemsSpacing: isMobile ? 2 : 4,
              itemDirection: 'left-to-right',
              itemWidth: isMobile ? 60 : 90,
              itemHeight: isMobile ? 16 : 20,
              itemOpacity: 0.85,
              symbolSize: isMobile ? 10 : 14,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          tooltip={({ point }) => (
            <Box
              sx={{
                background: colors.primary[700],
                padding: '9px 12px',
                border: `2px solid ${point.serieColor}`,
                borderRadius: '4px',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: colors.grey[100] }}>
                {point.data.xFormatted}
              </Typography>
              <Typography variant="body2" sx={{ color: point.serieColor, fontWeight: 'bold' }}>
                {point.serieId}: +{formatNumber(point.data.y)} DEC
              </Typography>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default DecBurnCategoriesChart;
